import { I18n } from "i18n-js";
import React from "react";
const { locale, translations } = window.I18nJs || { locale: 'en', translations: {} };
import { isDevelopmentOrTestEnv } from "./GeneralUtils";

const i18n = new I18n(translations);
i18n.locale = locale;

/**
 * Custom translation function that supports React components as placeholders.
 * @param {string} key - The translation key.
 * @param {object} options - The options object containing placeholders.
 * @returns {React.Element} - The translated text with React components.
 */
i18n.tWithReact = (key, options = {}) => {
  let reactComponents = {};

  // Iterate over the options to find React components
  Object.keys(options).forEach(key => {
    const obj = options[key];

    if (React.isValidElement(obj)) {
      reactComponents[key] = obj;
      options[key] = `%{${key}}`; // Replace React component with placeholder
    }
  });

  // Get the translated text with placeholders
  const translatedText = i18n.t(key, options);
  return replacePlaceholders(translatedText, reactComponents);
};

// --------------------------------------------------
// Examples for tWithTags
// cct- prefix refers to chronus custom text

// Translation: "We use AI to build your <cct-description>individualized Game Plan</cct-description>."
// const result = i18n.tWithTags("mentoring_goal_questionnaire.content.ai_disclaimer", {}, {
//   'cct-description': ({ children }) => <strong className="highlight">{children}</strong>
// });

// Translation: "For details, see our <cct-aipage /> for more information."
// const result = i18n.tWithTags("some.key", {}, {
//   'cct-aipage': () => <a href="/ai-policy" className="link">AI Policy</a>
// });

// Translation: "Welcome %{name}! Your <cct-highlight>personalized</cct-highlight> experience awaits."
// const result = i18n.tWithTags("welcome.message",
//   { name: "John" },
//   { 'cct-highlight': ({ children }) => <em className="emphasis">{children}</em> }
// );
// --------------------------------------------------
i18n.tWithTags = (key, options = {}, tagComponents = {}) => {
  // Get the translated text
  const translatedText = i18n.t(key, options);

  // Replace custom tags with React components
  return replaceCustomTags(translatedText, tagComponents);
};

/**
 * Replace placeholders in the translated text with React components.
 * @param {string} text - The translated text containing placeholders.
 * @param {object} replacements - The object containing React components.
 * @returns {Array} - An array of strings and React elements.
 */
const replacePlaceholders = (text, replacements) => {
  // Split the translated text by placeholders (e.g., %{placeholder})
  const parts = text.split(/(%\{[^}]+\})/g);

  // Map over the parts and replace placeholders with React components
  return parts.map((part, index) => {
    // Check if the part matches a placeholder pattern
    const match = part.match(/%\{(.+)\}/);

    // If a match is found and a corresponding React component exists, return the component
    if (match && replacements[match[1]]) {
      return <React.Fragment key={index}>{replacements[match[1]]}</React.Fragment>;
    }

    // Otherwise, return the part as is (string)
    return part;
  });
};

const replaceCustomTags = (text, tagComponents) => {
  // Regular expression to match custom tags: <tag>content</tag> or <tag />
  // Supports hyphenated tag names like cct-description, cct-aipage
  const tagRegex = /<(\w+(?:-\w+)*)(?:\s[^>]*)?>(.*?)<\/\1>|<(\w+(?:-\w+)*)(?:\s[^>]*)?\s*\/>/g;

  let lastIndex = 0;
  const parts = [];
  let match;
  let keyIndex = 0;

  // Find all custom tags in the text
  while ((match = tagRegex.exec(text)) !== null) {
    // Add text before the tag
    addTextBeforeTag(text, lastIndex, match.index, parts);

    // Process the matched tag
    const tagElement = processMatchedTag(match, tagComponents, keyIndex++);
    parts.push(tagElement);

    lastIndex = tagRegex.lastIndex;
  }

  // Add any remaining text after the last tag
  addRemainingText(text, lastIndex, parts);

  // If no tags were found, return the original text
  return parts.length === 0 ? [text] : parts;
};

const addTextBeforeTag = (text, lastIndex, currentIndex, parts) => {
  if (currentIndex > lastIndex) {
    const textBefore = text.slice(lastIndex, currentIndex);
    if (textBefore) {
      parts.push(textBefore);
    }
  }
};

const processMatchedTag = (match, tagComponents, keyIndex) => {
  // Determine tag name and content
  const tagName = match[1] || match[3]; // match[1] for content tags, match[3] for self-closing
  const content = match[2]; // content between opening and closing tags (undefined for self-closing)

  // Get the React component for this tag
  const TagComponent = tagComponents[tagName];

  if (TagComponent) {
    return createTagComponent(TagComponent, content, keyIndex);
  }

  // Handle unknown tags based on environment
  return handleUnknownTag(tagName, content, match[0]);
};

const handleUnknownTag = (tagName, content, originalMatch) => {
  if (isDevelopmentOrTestEnv()) {
    // In development, throw an error to help developers catch missing tag mappings
    const errorMessage = content !== undefined
      ? `Missing tag component mapping for '<${tagName}>' in tWithTags. Content: "${content}"`
      : `Missing tag component mapping for self-closing tag '<${tagName} />' in tWithTags.`;

    console.error(`ChrI18n Error: ${errorMessage}`);
    throw new Error(`ChrI18n: ${errorMessage}`);
  } else {
    // In production, gracefully handle by returning just the content (strip the tags)
    // For content tags: <unknown>content</unknown> → "content"
    // For self-closing tags: <unknown /> → "" (empty string)
    return content !== undefined ? content : '';
  }
};

const createTagComponent = (TagComponent, content, keyIndex) => {
  // If it's a self-closing tag or has no content, render component without children
  if (content === undefined || content === '') {
    return (
      <React.Fragment key={`tag-${keyIndex}`}>
        <TagComponent />
      </React.Fragment>
    );
  }

  // If it has content, render component with content as children
  return (
    <React.Fragment key={`tag-${keyIndex}`}>
      <TagComponent>{content}</TagComponent>
    </React.Fragment>
  );
};

const addRemainingText = (text, lastIndex, parts) => {
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      parts.push(remainingText);
    }
  }
};

export default i18n;
